// ============================================================================
// logo-structure.js — structure-aware auto-styling (B1)
// ============================================================================
// Palette extraction (palette-extract.js) answers "WHICH colours are in the
// logo". This module answers the SPATIAL question palette-extract can't:
// "how are they LAYERED?" — is the mark a FILL wrapped in ordered inner→outer
// OUTLINES (the classic varsity letter: teal fill / white / rust), and if so,
// in what order and how thick? The answer drives the plate's TEXT lockup so the
// school name + weight number echo the logo's own letter treatment
// (CUSTOM-WEIGHT-DESIGNER-V2-PLAN.md §4 — "their brand is the hero").
//
// The 1–4 mitered-outline render engine already EXISTS (render-engine.js
// emitStyledText, weight-designer-element-model) — the gap this fills is the
// AUTO DECISION of what outlines to apply and how thick, from the pixels.
//
// APPROACH (needs spatial reasoning, not a histogram):
//   1. Label each foreground pixel to its nearest extracted swatch (Lab).
//   2. Distance-transform: how DEEP is each pixel from the silhouette edge?
//      Outlines are concentric shells, so depth ORDERS them — the fill is
//      deepest, the outermost outline hugs the edge.
//   3. Order layers by median depth (deep → shallow) = fill, inner…outer.
//   4. VERIFY that order by boundary ADJACENCY (does each layer actually wrap
//      the next one in?). This is what rejects a full CREST (Bauer's Elite):
//      a crest's colours interleave — no colour cleanly wraps another — so it
//      falls back to the plain fill + single-outline default instead of
//      inventing a bogus outline stack.
//   5. Thickness from each shell's radial depth-span, normalised to the model.
//
// STRUCTURE FOR TESTABILITY: `analyzeStructure(imageData, swatches, opts)` is a
// pure function (no DOM) run in plain Node against synthetic ImageData. Only
// `extractLogoStructure` needs a browser (decode + canvas + getImageData).
// ============================================================================

import { srgbToLab, deltaE, censusTones, dropSeamTones, adoptSpelling } from './palette-extract.js';
import { measureInkPieces, measureToneTouch, measureToneSubstance } from './bg-detect.js';

/**
 * @typedef {Object} StructureLayer
 * @property {string} hex
 * @property {[number,number,number]} rgb
 * @property {number} thickness  - render-engine outline units (0 for the fill).
 * @property {number} area       - pixel share of the foreground (0..1).
 * @property {number} depth      - median normalised depth (0 edge .. 1 core).
 */

/**
 * @typedef {Object} LogoStructure
 * @property {'high'|'low'} confidence
 * @property {StructureLayer|null} fill
 * @property {StructureLayer[]} outlines  - inner→outer (outlines[0] hugs the fill).
 * @property {string} [reason]            - why confidence is 'low' (debug).
 */

const DEFAULTS = {
  alphaThreshold: 128,   // 0-255; below this a pixel is background (not part of the mark)
  minLayerFraction: 0.03,// a swatch must own this share of foreground to be a real LAYER
                         // (drops anti-alias strays the ΔE merge left behind)
  maxLayers: 4,          // fill + up to 3 outlines (render engine caps outlines at 4)
  defaultOutline: 2.5,   // render units a "typical" outline maps to (matches createElement)
  maxOutline: 4,         // per-outline clamp (render units)
  minOutline: 1,
  // Ridge samples a medial-axis width needs before its median is a measurement rather than noise.
  // Was an inline 16 in the `strokeFrac` pass; named when WD 438 started asking it PER BAND too.
  minRidgeSamples: 16,

  // ── fill + shell-walk tunables ────────────────────────────────────────────
  minChroma: 12,         // CIELAB chroma at/above which a colour is a chromatic "hero"
                         // eligible to be the FILL — neutrals (black/gray/white) are
                         // borders or interior detail, never the fill (Bauer: blue, not black)
  /* …unless that leaves the mark with NO bands at all — see the retry at the fill pick.
     ⛔ There is deliberately no SIZE bar on that retry. `probe-structure-decline.mjs` prints the
     corpus sorted by how much of the mark the neutral holds and it is a continuum — 0.560,
     0.508, 0.453, 0.449, 0.438, 0.429 … 0.293, with the marks Ryan approved interleaved with
     the ones he flagged — so any bar in it would be a coincidence. `neutralFill: false`
     restores the pre-WD-432 behaviour, which is how that probe scores BEFORE against the app's
     own record. */
  /* How much of its OWN bounding box the mark's largest connected piece must cover before that piece
     counts as a solid BADGE rather than line art (see `colorBorneDetail`).
       MEASURED over the 29-logo drop folder (`tools/probe-colorborne-corpus.mjs`). Of the 26 marks
     that pass the "one piece, two tones" clauses, the density column reads 1.000 nineteen times,
     then 0.997 (McDonald's) · 0.785 (Philadelphia PD) · 0.726 (E Logo) · 0.719 (Body State U) ·
     0.644 (Bauer) · 0.595 (Springboro) — and then **0.226, the Layton spartan, alone**. A 2.6x gap
     with one mark under it, so any bar inside it splits the corpus identically and 0.41 is its
     midpoint: 0.18 of clearance either side. ⚠ A future mark landing near this wants its own
     evidence, not a nudge — the discipline `bandWrapMin` and `letteringMaxShare` both state. */
  colorBorneDensityMin: 0.41,
  fillTouchMin: 0.08,    // a band whose boundary faces the FILL at least this much ABUTS it, so a
                         // single-tone recolour must not swallow it (see `touchesFill` below). A
                         // keyline runs ~half its perimeter against the fill and a detached ring
                         // ~none, so anything in between still lands on the safe side.
  // LETTERING vs KEYLINE (Ryan 2026-07-25) — an outermost shell in many pieces with NO dominant one
  // is a word or scattered detail, not a border. Both factors are required: measured over 32
  // high-confidence logos, a genuine keyline is one continuous piece (maxShare 0.86–1.00, most
  // ≥0.98) while lettering has none (GGR 0.082, Blue Sky wordmark 0.195, Irwin 0.199). Parts alone
  // demoted 23 of 43 — "Dumbbell Example" is 12 pieces at 0.966, a solid ring with a few nicks, and
  // a raw count can't tell it from GGR's 16 at 0.082. maxShare is what separates them; the nearest
  // survivor is 0.216, so the 0.20 line has real clearance on both sides.
  letteringParts: 8,
  letteringMaxShare: 0.2,

  // ── DOES THE INNERMOST BAND WRAP THE FILL AT ALL? (Ryan 2026-08-13) ───────────────────────────
  // The pair behind the `inner-band-wraps-nothing` demotion — see the long note at that check for
  // the corpus these came off and why the obvious mass-only rule is false. In short: a band that
  // faces the fill with less than `bandWrapMin` of its perimeter is not wrapping anything, and when
  // it also carries `bandIsTheMarkArea` or more of the ink it IS the mark rather than trim around
  // one, so the whole fill-plus-outlines reading is inside-out and the mark falls back to plain
  // palette styling.
  bandWrapMin: 0.02,        // MEASURED: innermost-band shares run 0.0036 (Cherry Valley v2, the
                            // broken one) then 0.0450, 0.0636, 0.0834, … — a 12× gap, so any bar in
                            // (0.0036, 0.0450) isolates it. ⛔ NOT `fillTouchMin` (0.08): that is
                            // calibrated for a different decision and, at 0.08, Bears clears by
                            // 0.0034. Re-measure rather than nudge if a future mark lands near.
  bandIsTheMarkArea: 0.5,   // The DAMAGE term, not the discriminator (adjacency is). A band that
                            // wraps nothing is misread at any size; it is only worth discarding an
                            // outline stack over when the band is most of the mark. Nothing on the
                            // corpus turns on the exact value — `bandWrapMin` alone already isolates
                            // Cherry Valley v2 — so this exists to keep a future detached FLECK from
                            // costing a legitimate mark its structure.
  minPartPx: 12,         // pieces smaller than this are anti-alias crumbs / a dotted rule's dots,
                         // not glyphs — ignored so a soft edge can't fake a lettering verdict.
  outerBgMin: 0.2,       // the OUTERMOST shell must be the exposed silhouette (touch background
                         // at least this much) — else it's interior detail, not a border. Set at
                         // 0.2 so a crest whose rim is interrupted by tabs/points that poke a
                         // deeper shell out to the edge (Bauer: black tabs through the gray rim,
                         // gray bg-share ~0.25) still reads as a border, not interior detail.
  // ── A BURIED innermost band is DROPPED, not demoted (Ryan 2026-08-23, Abbey Athletics) ─────────
  // Its crest's interior gray out-ran the fill at its depth bin and became the "innermost band" —
  // and Ryan's own session data convicts it: boundary 2800, of which 2226 faces the black band,
  // 574 the white band, 0 the fill, 0 the background. A ring that touches neither the fill it
  // supposedly wraps NOR the background it supposedly separates from is buried interior detail;
  // as a band it seeded a third text ring AND flipped `logoGapFromStructure` (innermost-detached
  // IS the gap predicate), putting a phantom plate-coloured gap around the text. Dropping just the
  // buried member keeps the mark's REAL structure (Abbey's black+white), where the wraps-nothing
  // demotion below would junk the whole reading.
  //   Both bars sit against the measured keeps: EE Logo's innermost is at fill-touch 0.0636 and
  // Elite Edge Again's at 0.0450 (both deliberately kept); Hanover's detached keyline faces
  // background massively through its moat; E Logo's white touches its fill at 0.4859. Abbey's
  // gray measures 0.0 on both axes — open space on both sides.
  buriedBandFillMax: 0.02,  // faces the fill less than this…
  buriedBandBgMax: 0.02,    // …AND the background less than this → buried, dropped from the chain
};

// Sentinel labels used while walking boundaries.
const BG = -1;   // background / out of bounds
const OTHER = -2; // a foreground pixel whose swatch didn't make the significant-layer cut

/**
 * THE RASTER'S OWN INK LIST - which tones the mark actually wears, measured from pixels.
 *
 * ⛔ THE MEASUREMENT ITSELF MOVED TO `palette-extract.js` ON 2026-09-18 (WD 430) and this is the
 * adapter that feeds it RGBA. It moved because the PALETTE needs the same census one step earlier
 * - to ask whether its own swatches are colours the pixels wear - and "which tones does this mark
 * wear" answered twice, in two files, is the exact shape of bug `shownLogoTones` exists to end.
 * What it does and why it keeps the most populous bin's colour instead of re-averaging is
 * documented there.
 *
 * ⚠ Depends on ALPHA ONLY, which is what lets it run BEFORE the labelling pass it now feeds.
 *
 * @returns {Array<{hex:string, rgb:[number,number,number], c:number}>} population-descending
 */
function censusInks(data, n, alphaThreshold) {
  const opaque = [];
  for (let p = 0; p < n; p++) {
    if (data[p * 4 + 3] < alphaThreshold) continue;
    opaque.push([data[p * 4], data[p * 4 + 1], data[p * 4 + 2]]);
  }
  return censusTones(opaque);
}

/**
 * Adopt the BRAND's spelling of a tone the palette already knows about.
 *
 * ⛔ ALSO MOVED to `palette-extract.js` as `adoptSpelling` (WD 430) - the census rebuild there
 * needs the identical rule, for the identical reason, and had started to grow a second copy of it.
 * The raster decides WHICH tones exist; where a raster tone and a brand swatch are the same colour
 * (ΔE < 12) the swatch's hex is the better label, because it is the spelling on the buyer's chips.
 * A raster tone with NO palette match keeps its own honest hex - that is the ink the palette
 * dropped, and reporting it is the entire fix.
 *
 * ⚠ `dropSeamTones` moved with them, and its ⭐ note is the one to read before touching either:
 * webster's navy at 39% of the ink cannot be caught by the seam rule, and that is the proof the
 * rule is safe to run over a census.
 */
function snapToPalette(inks, swatches) {
  return adoptSpelling(inks, swatches, 12).map((t) => ({ hex: t.hex, rgb: t.rgb }));
}
/**
 * Analyse a cut-out logo's fill/outline structure. Pure — no DOM.
 *
 * @param {{data:Uint8ClampedArray|number[], width:number, height:number}} imageData
 *        RGBA pixels with the BACKGROUND ALREADY TRANSPARENT (a keyed / alpha logo).
 * @param {Array<{hex:string, rgb:[number,number,number]}>} swatches
 *        The extracted distinct colours (palette-extract's `all`), any order.
 * @param {Partial<typeof DEFAULTS>} [opts]
 * @returns {LogoStructure}
 */
export function analyzeStructure(imageData, swatches, opts = {}) {
  const o = { ...DEFAULTS, ...opts };
  // ⚠ CARRIED ON THE LOW PATH TOO (Ryan 2026-07-28: "no visible change, still created 4x flat
  // circular logo versions"). Whether a mark's detail is colour-borne is a fact about the ARTWORK; it
  // has nothing to do with whether this function manages to find outline BANDS in it. The Denver
  // badge returns `low('no-band')` — concentric rings are not the border-and-fill shape this looks
  // for — and every low return was a bare object, so the one measurement that could have suppressed
  // the flatten was computed and then dropped on the floor. Declared here so it survives whichever
  // exit the analysis takes.
  let colorBorneDetail = false;
  // Carried on the low path for the same reason `colorBorneDetail` is: it is a fact about the
  // ARTWORK, and a concentric badge — the very mark whose fine ruling this exists to notice — returns
  // low confidence every time.
  let strokeFrac = 0;
  /* ⭐ THE SAME MEASUREMENT, PER LAYER (WD 438) — the absolute weight of EACH band's linework, so a
     mirrored ring can be weighed against the band it mirrors rather than against the mark's median
     stroke. Carried on the low path with `strokeFrac` for the same reason it is. See the ridge pass
     below, and `seedOutlinesFrom` for what spends it. */
  let strokeFracByLayer = null;
  // Diagnostic only — the three numbers behind `colorBorneDetail`, so a threshold on any of them can
  // be re-measured against the corpus without a second implementation of the flood fill
  // (`tools/probe-colorborne-corpus.mjs`). Nothing reads it to make a decision.
  let solidBlobDiag = { share: 0, tones: 0, density: 0 };
  // Who touches whom, by HEX — the fact behind "would repainting tone A to tone B fuse two touching
  // regions?" (Ryan 2026-08-18, Edison + Mission Patriots). Computed from the same `boundaryAdjacency`
  // tally the band analysis has always used; null until step 4 has run, so the early low exits (no
  // image / one colour / flat mask) — marks where a repaint collision cannot exist — carry none.
  let toneTouches = null;
  // Full-pixel tone CENSUS, independent of the recorded swatches (WD 337, Ryan's corpus review:
  // CSI's thin black band exists in the pixels but in NO recorded list — the palette dropped it
  // and the nearest-swatch labelling above can only ever answer with a recorded tone. A repaint
  // target the mark already wears is what the Kenyon test needs to see). Null until the mask has
  // run; carried on BOTH exits like `colorBorneDetail`.
  let traceTones = null;
  let soloTones = null;   // R13 — tones that are the WHOLE of a detached piece (see the flood below)
  /* The mark's INK in connected pieces, each labelled with the tone it is mostly made of — the
     measurement behind Ryan's free-floating exception (`measureInkPieces`, bg-detect). Carried on
     the low path for the reason `traceTones` and `soloTones` are, and it is not a small one here:
     BOTH marks he judged the rule on return low confidence, so a high-only field would answer
     nothing on the very cases the rule exists for. */
  let inkPieces = null;
  /* WD 447 - which tones share a shape with which, for Ryan's neutral-touch rule.
     /!\ NOT `toneTouches`, which is a DIFFERENT measurement already on this object (the
     adjacency of layers). Naming this one the obvious thing would have shadowed it. */
  let toneNeighbours = null;
  /* WD 449 - per tone, how much of it survives one erosion (is it a BODY or a STROKE), for
     rule 3. Reported, never decided here: the pairing with share lives at the call site. */
  let toneSubstance = null;
  /* WD 432: the probe seam at the foot of this file asks for the layer table, and every decline
     path below returns through `low`. A closure keeps it out of the hot path — `__layers` is
     null unless `__emitLayers` was passed, and the app never passes it. */
  let __layers = null;
  const low = (reason) => ({ confidence: 'low', fill: null, outlines: [], reason, colorBorneDetail, strokeFrac, strokeFracByLayer, solidBlob: solidBlobDiag, toneTouches, traceTones, soloTones, inkPieces, toneNeighbours, toneSubstance, ...(o.__emitLayers ? { __layers } : {}) });
  if (!imageData || !imageData.data || !imageData.width || !imageData.height) return low('no-image');
  if (!Array.isArray(swatches) || swatches.length < 2) return low('too-few-swatches');

  const { data, width: w, height: h } = imageData;
  const n = w * h;

  /* ══ THE LABELLING BASIS IS THE RASTER, NOT THE BRAND PALETTE (WD 424) ═══════════════════════
   * ⛔ THIS USED TO READ `swatches` — i.e. `palette.all` — AND THAT IS WHERE THE CENSUS BUG LIVED.
   *
   * `palette-extract` answers "what are this brand's colours?", and to answer it well it DELETES
   * things: a cluster near the keyed-out background (`bgResidualTol`), a colour that reads as a
   * fringe (`findRimArtifacts`), a colour that never won a median-cut bucket (`maxColors: 8`), a
   * colour that sits between two others (`dropBlends`). Every one of those deletions is CORRECT
   * for the chips the buyer sees.
   *
   * They are catastrophic here, because this function labels EVERY PIXEL against that list. Delete
   * a real ink and its pixels do not disappear — they are re-assigned to the nearest survivor, and
   * the survivor inherits the deleted ink's mass. Measured on the corpus:
   *
   *   websterathletics   navy #161147 is 51% of the raster, deleted by `bgResidualTol` (the art is
   *                      dark on a black background, so the KEY COLOUR IS A LOGO COLOUR — the ink
   *                      sits 65 units from the key, polkeagles' black sits 2).
   *                      brown #754514 is 0.66% of the pixels and was reported at 56.0% of the mark.
   *   polkeagles         black #010101 47%, deleted. cream #E8D5A9 — ONE pixel in 42,749 — 50.5%.
   *   mountmercy         reported fill #033C26 appears in ZERO of 4,667 pixels: it is the average of
   *                      the green and the black, because median-cut spent four of its eight buckets
   *                      on near-duplicates of one navy and never gave the black a bucket.
   *
   * 19 of 132 marks (14%) lost a tone worth >=10% of the mark. Ryan caught one by eye on
   * 120039004726 — *"TODAY is slightly darker than the green used on the actual logo"* — which was
   * exactly a median-cut centroid with black averaged into it.
   *
   * ⭐ So the two jobs are SPLIT. The palette keeps all four filters and keeps feeding the chips.
   * The basis below is built from the pixels: which tones the mark actually WEARS, and in what
   * share. The palette is still consulted for ONE thing — the canonical hex. Where a raster tone
   * agrees with a brand swatch (dE < 12) the swatch's hex is adopted, so a tone the palette knows
   * about still reports the brand's own spelling and the chips stay consistent; a tone the palette
   * DROPPED keeps its honest raster hex, which is the whole point.
   *
   * ⚠ `opts.inkBasis = 'palette'` restores the old behaviour verbatim. It exists so the corpus
   * diff can be taken without a git stash — NOT as a fallback. Nothing ships reading it. */
  const rasterInks = censusInks(data, n, o.alphaThreshold);
  /* ⛔ THE BAND ANALYSIS KEEPS THE PALETTE BASIS, AND THAT IS A CORRECTION TO THE FIRST CUT.
   * Swapping the basis for BOTH the bands and the tone table moved 45 of 101 corpus verdicts and
   * cost 14 marks their structure against 5 that gained one — because `outerBgMin`, `bandWrapMin`
   * and their neighbours are constants CALIBRATED against the palette labelling. A cleaner basis
   * shifts what those thresholds measure, so a change that is right in principle reads as a
   * regression until every one of them is re-measured. That is its own pass.
   *   The census bug does not need it. The number's colour flows through the TONE TABLE
   * (`toneTouches` -> `markDominantTone`), not through the bands — so only the table moves, which
   * is the split this module already makes for `sigWide` ("only the TABLE widens"). The outline
   * mirroring keeps behaviour it was tuned for, bit for bit. */
  const basis = swatches;
  const rasterBasis = (o.inkBasis !== 'palette' && rasterInks.length >= 2)
    ? snapToPalette(dropSeamTones(rasterInks), swatches) : null;
  const labs = basis.map((s) => srgbToLab(s.rgb));

  // 1. Foreground mask + nearest-ink label per pixel.
  const label = new Int16Array(n); // basis index, or BG
  const counts = new Array(basis.length).fill(0);
  let fgCount = 0;
  for (let p = 0; p < n; p++) {
    const a = data[p * 4 + 3];
    if (a < o.alphaThreshold) { label[p] = BG; continue; }
    const lab = srgbToLab([data[p * 4], data[p * 4 + 1], data[p * 4 + 2]]);
    let best = 0, bestD = Infinity;
    for (let k = 0; k < labs.length; k++) {
      const d = deltaE(lab, labs[k]);
      if (d < bestD) { bestD = d; best = k; }
    }
    label[p] = best;
    counts[best] += 1;
    fgCount += 1;
  }
  if (fgCount < 64) return low('empty-mask');

  // The census itself: 16-step RGB bins over the raw opaque pixels, kept at >=0.15% of the ink,
  // merged within a small Delta-E so one tone split across bins reads as one row. Consumers put
  // their own share bar on top (a veto reads >=0.4% — below the table's 3% floor, above most
  // anti-aliasing ridges; a phantom blend row can only ever REFUSE a repaint, which falls back to
  // the reversible add-ring, so the bias is safe by construction).
  // ⚠ ONE census, two consumers. `rasterInks` (above) is this same measurement — it is what the
  // labelling basis is now built from, so the reported tones and the tones the pixels were
  // labelled against can no longer disagree. Kept as its own field because consumers put their
  // own share bar on top (a veto reads >=0.4% — below the table's 3% floor, above most
  // anti-aliasing ridges; a phantom blend row can only ever REFUSE a repaint, which falls back to
  // the reversible add-ring, so the bias is safe by construction).
  traceTones = rasterInks.map((t) => ({ hex: t.hex, share: +(t.c / fgCount).toFixed(4) }));
  /* ⚠ MEASURED OFF `traceTones`, NOT off `sig` — the census is the honest list of what the mark
     WEARS (WD 424), and the piece test is asking which tone a piece is made of. The 3% floor is
     `minLayerFraction`, the module's own "owns a meaningful share of the mark"; no new constant. */
  inkPieces = measureInkPieces(data, w, h, traceTones.filter((t) => t.share >= o.minLayerFraction));
  /* WD 447 - the same flood WITHOUT the piece test's size bar or its dominant-tone label, on
     the same census rows, because the neutral-touch rule asks a question those two answers
     hide: the wreath's pink owns fifteen whole shapes and every one is under the bar. */
  toneNeighbours = measureToneTouch(data, w, h, traceTones.filter((t) => t.share >= o.minLayerFraction));
  toneSubstance = measureToneSubstance(data, w, h, traceTones.filter((t) => t.share >= o.minLayerFraction));

  // Which swatches are real LAYERS (own a meaningful share of the mark)? Cap to
  // maxLayers by population; everything else is folded into OTHER for adjacency.
  const sig = counts
    .map((c, k) => ({ k, c }))
    .filter((x) => x.c / fgCount >= o.minLayerFraction)
    .sort((a, b) => b.c - a.c)
    .slice(0, o.maxLayers)
    .map((x) => x.k);
  if (sig.length < 2) return low('single-colour'); // 1 clean colour → no outline structure
  const isSig = new Set(sig);

  // Re-map every foreground pixel to a SIGNIFICANT layer index (0..sig.length-1),
  // OTHER for insignificant colours. slabel is what the rest of the analysis uses.
  const sigIndexOf = new Map(sig.map((k, i) => [k, i]));
  const slabel = new Int16Array(n);
  for (let p = 0; p < n; p++) {
    if (label[p] === BG) { slabel[p] = BG; continue; }
    slabel[p] = isSig.has(label[p]) ? sigIndexOf.get(label[p]) : OTHER;
  }

  // ── IS THIS MARK'S DETAIL CARRIED BY ITS SHAPE, OR ONLY BY ITS COLOUR? ────────────────────────
  // (Ryan 2026-07-28) "it took a 4-colour logo which has no gaps in it at all — all 4 colours
  // touching — and decided to create 4 solid-fill versions of it. On this logo that will never work
  // no matter what the colours are. You're reducing an intricate detailed badge into a solid fill
  // circle. No one will ever want that."
  //
  // Exactly right, and it is a better question than any of the contrast rules downstream were asking.
  // Flattening a mark to one colour keeps everything the SILHOUETTE says and destroys everything the
  // COLOUR BOUNDARIES say. Whether that is a useful offer or a guaranteed ruin depends entirely on
  // which of the two the mark uses to carry its identity:
  //
  //   · a WORDMARK is many separate opaque islands — letters, with background between them. Flatten
  //     it and every letterform survives, because the shape was doing the work. Worth offering.
  //   · a ONE-TONE mark has no colour boundaries to lose; flattening is just recolouring. Worth
  //     offering (and it never reaches here — `sig.length < 2` returns above).
  //   · a BADGE is ONE contiguous blob whose ring, field, lettering and mascot exist only as colour
  //     boundaries inside it. Flatten it and a circle is all that is left. Never worth offering.
  //
  // So: find the largest connected run of foreground and count how many significant layers live
  // inside it. Two or more tones sharing one component, and that component holding most of the mark,
  // means the detail is colour-borne. A four-square-inch flood fill of any colour is the only thing a
  // flatten can produce from it.
  //
  // Deliberately measured on the FOREGROUND MASK rather than per layer: `componentCount` below asks
  // "how many pieces is this ONE layer in", which is a different question and cannot see that four
  // layers are all pieces of the same blob.
  /* ⭐ THE SAME WALK ALSO ANSWERS R13 (Ryan's pass-one review, 2026-09-13) — which tones are the
   * WHOLE of a detached piece of this mark. Two of his flags are one sentence:
   *     carrolllynx cfg3  *"this logo is an anomaly in that it has 2 free-floating sections. One
   *                        section is COMPLETELY white, and another section is multiple colours
   *                        INCLUDING white… teach the tool to simply not use a white background
   *                        colour on any of the plates."*
   *     ee-logo cfg2      *"Another example of a free-floating white-only section of the logo,
   *                        which got placed on a white plate."*
   *   A one-tone piece has no internal boundary to fall back on: put it on a plate of its own
   * colour and it does not go subtle, it DISAPPEARS. Every other rescue in the app works on the
   * tone (repaint it, ring it, peel it) and each of those would rewrite the multi-coloured piece
   * beside it that wears the same colour legitimately — Ryan saw that too and said so. The cheap,
   * non-destructive half is simply not to DEAL that plate colour, which is what `p.logoSoloTones`
   * feeds (`dealPlateDefaults`).
   *   ⚠ ON THE FOREGROUND MASK AND SHARING THIS FLOOD DELIBERATELY. `componentCount` asks "how many
   * pieces is one LAYER in", which cannot see that a piece is one-toned; this walk already has the
   * per-component tone set that question needs, and re-flooding for it would be a second answer to
   * a question this loop is already answering. Size bar is `minLayerFraction`, the module's own
   * "owns a meaningful share of the mark" — no new constant. */
  const soloPx = new Map(); // significant-layer index -> px living in ONE-TONE detached pieces
  const solidBlob = (() => {
    const seen = new Uint8Array(n);
    const stack = new Int32Array(n);
    let best = 0;
    let bestTones = 0;
    let bestBox = 0;
    for (let start = 0; start < n; start++) {
      if (seen[start] || slabel[start] === BG) continue;
      let sp = 0;
      stack[sp++] = start;
      seen[start] = 1;
      let size = 0;
      const tones = new Set();
      let x0 = w, x1 = -1, y0 = h, y1 = -1;
      while (sp > 0) {
        const q = stack[--sp];
        size += 1;
        if (slabel[q] !== OTHER) tones.add(slabel[q]);
        const x = q % w, y = (q / w) | 0;
        if (x < x0) x0 = x; if (x > x1) x1 = x;
        if (y < y0) y0 = y; if (y > y1) y1 = y;
        if (x > 0 && !seen[q - 1] && slabel[q - 1] !== BG) { seen[q - 1] = 1; stack[sp++] = q - 1; }
        if (x < w - 1 && !seen[q + 1] && slabel[q + 1] !== BG) { seen[q + 1] = 1; stack[sp++] = q + 1; }
        if (y > 0 && !seen[q - w] && slabel[q - w] !== BG) { seen[q - w] = 1; stack[sp++] = q - w; }
        if (y < h - 1 && !seen[q + w] && slabel[q + w] !== BG) { seen[q + w] = 1; stack[sp++] = q + w; }
      }
      if (size > best) { best = size; bestTones = tones.size; bestBox = (x1 - x0 + 1) * (y1 - y0 + 1); }
      // R13: a piece whose ink is a SINGLE significant tone. `OTHER` pixels (anti-alias crumbs and
      // sub-3% strays) are not tones for this purpose — they are exactly what `tones` already skips.
      if (tones.size === 1) { const t = [...tones][0]; soloPx.set(t, (soloPx.get(t) || 0) + size); }
    }
    // 0.60 of the foreground: high enough that a mark whose colours sit in separate pieces is never
    // caught, low enough that a badge with a detached ⓡ or a stray dot beside it still is.
    //
    // `density` = how much of its OWN bounding box that piece actually covers. See the flag below:
    // "one piece, two tones" cannot tell a badge from line art, and this is what does.
    return { share: fgCount ? best / fgCount : 0, tones: bestTones, density: bestBox ? best / bestBox : 0 };
  })();
  solidBlobDiag = solidBlob;
  soloTones = [...soloPx.entries()]
    .filter(([, px]) => fgCount && px / fgCount >= o.minLayerFraction)
    .map(([t, px]) => ({ hex: basis[sig[t]].hex, share: +(px / fgCount).toFixed(4) }))
    .sort((a, b) => b.share - a.share);
  /* The whole point in one flag, so no consumer has to re-derive the reasoning.
   *
   * ⛔ "ONE PIECE, TWO TONES" CANNOT TELL A BADGE FROM LINE ART, AND IT WAS ANSWERING FOR BOTH (Ryan
   * 2026-08-15, the Layton spartan): *"when I set the plate to the logo's navy, shouldn't the logo
   * in its entirety change to the light blue? … the Wallabies logo is working fine, but the Layton
   * logo isn't."* Two two-tone marks, opposite outcomes, and this flag is the whole difference —
   * `applyVanishingLogoDefaults` treats it as a hard "never flatten this".
   *   The Wallabies passes because its tones sit in SEPARATE pieces (gold kangaroo, green wordmark),
   * so the largest component is one-toned. Layton's navy strokes and blue shading touch, making one
   * two-toned component that is ~all of the mark — the badge test exactly, on a mark that is not a
   * badge. The reasoning above is still right; what it lacked was the second half of its own
   * argument. A badge is one contiguous blob that is also SOLID: flatten it and the silhouette is a
   * disc, because the silhouette was never carrying anything. Line art is one contiguous blob that
   * is mostly HOLES: flatten it and every stroke, cutout and counter survives, which is precisely
   * what Ryan is asking for.
   *
   * ⚠ 0.5 IS MEASURED — density is the largest piece's share of its own bounding box, and the corpus
   * splits with a wide gap (`tools/probe-colorborne-corpus.mjs`, 29 marks). See that table before
   * moving it; a mark near the bar wants its own evidence, not a nudged threshold. */
  colorBorneDetail = solidBlob.tones >= 2 && solidBlob.share >= 0.60
    && solidBlob.density >= o.colorBorneDensityMin;

  // ── HOW HEAVY IS THIS MARK'S LINEWORK? (Ryan 2026-07-28) ─────────────────────────────────────
  // "the logo utilises a very thin white outline followed by a medium orange inside of that,
  // whereas the text and numbers, when adopting outlines, immediately grab a pretty thick one."
  //
  // The text's default ring was the constant 2.5 with no measurement behind it — and, awkwardly, the
  // MEASURED path could not have helped either: the band-thickness pass below normalises so a logo's
  // MEDIAN band always maps to 2.5, so it preserves the ratios between one logo's own bands and
  // discards the absolute weight of its linework entirely. A hairline-ruled badge and a chunky
  // varsity letter both come out anchored at 2.5.
  //
  // So measure the artwork's stroke vocabulary directly, by its MEDIAL AXIS. Distance-transform to
  // the nearest colour boundary (not the silhouette — the interesting edges are internal), keep the
  // ridge pixels where that distance is locally maximal, and take the median of twice it. Every ridge
  // point sits at the middle of a band and reports that band's full width, so the median is weighted
  // by ridge LENGTH: a long thin keyline contributes far more samples than a small fat blob, which is
  // exactly the weighting the eye uses when it calls a mark "finely ruled".
  //   Reported as a FRACTION of the mark's long edge, so it is a statement about the artwork and not
  // about the raster it was measured on.
  strokeFrac = (() => {
    const bd = boundaryDepth(slabel, w, h);
    let max = 0;
    for (let p = 0; p < n; p++) if (bd[p] > max) max = bd[p];
    if (max <= 0) return 0;
    const widths = [];
    /* ⭐ AND THE SAME SAMPLES, BUCKETED BY LAYER (WD 438, Ryan's pass-seven notes: *"Need to make
       sure the thickness of the outline on the number matches the logo as best as possible"* — six
       plates over three marks, plus *"the black outline on after is too prominent"*).
         Every ridge point already reports the full width of the band it sits in; splitting them by
       `slabel` asks the SAME question of one band instead of the whole mark. That is the number
       `seedOutlinesFrom` has never had: the band pass below normalises so the MEDIAN band lands on
       2.5, which keeps a mark's internal ratios and discards the absolute — the exact fact
       `presentedOuterOutline` writes around when it refuses to mirror a band's stored weight.
         ⛔ AND IT MUST BE THE MEDIAL AXIS, NOT AREA ÷ CONTOUR. The band pass's own `widthOf` is a
       ratio-only instrument and reading it as an absolute is wrong by an order of magnitude on real
       marks: measured over the corpus capture, it puts `120051002169`'s band at 32% of the mark's
       long edge (16.8 viewBox units) and `120039004726`'s at 18%, because a region that meets its
       inner neighbour along a short seam divides a large area by a small contour. It is honest
       about RATIOS between one mark's bands, which is all the pass below asks of it. */
    const byLayer = sig.map(() => []);
    for (let y = 1; y < h - 1; y++) {
      for (let x = 1; x < w - 1; x++) {
        const p = y * w + x;
        const d = bd[p];
        if (d <= 0) continue;
        // Ridge = a local maximum along BOTH axes (a plateau counts, hence >=). A band's centre line
        // satisfies this; its flanks do not.
        if (d >= bd[p - 1] && d >= bd[p + 1] && d >= bd[p - w] && d >= bd[p + w]
            && (d > bd[p - 1] || d > bd[p + 1] || d > bd[p - w] || d > bd[p + w])) {
          widths.push(2 * d);
          const li = slabel[p];
          if (li >= 0 && byLayer[li]) byLayer[li].push(2 * d);
        }
      }
    }
    /* ⚠ THE SAME 16-SAMPLE FLOOR, ASKED PER BAND. Below it the median is noise rather than a
       measurement, and a ring weighed off noise is worse than one weighed off the mark's median
       stroke — which is what a null here falls back to. */
    strokeFracByLayer = byLayer.map((ws) => {
      if (ws.length < o.minRidgeSamples) return null;
      ws.sort((a, b) => a - b);
      return pct(ws, 0.5) / Math.max(w, h);
    });
    if (widths.length < o.minRidgeSamples) return 0; // too little structure to characterise — caller falls back
    widths.sort((a, b) => a - b);
    return pct(widths, 0.5) / Math.max(w, h);
  })();

  // 2. Distance transform — chamfer 3/4, out-of-bounds treated as BACKGROUND so a
  //    mark trimmed flush to the canvas still reads its edge as the silhouette.
  const depth = chamferDepth(slabel, w, h); // in pixels (fractional via /3 below)
  let maxDepth = 0;
  for (let p = 0; p < n; p++) if (depth[p] > maxDepth) maxDepth = depth[p];
  if (maxDepth <= 0) return low('flat-mask');

  // 3. Per-layer stats: pixel share, chroma, median depth (0 edge .. 1 core).
  const layers = sig.map((k, i) => {
    const ds = [];
    for (let p = 0; p < n; p++) if (slabel[p] === i) ds.push(depth[p] / maxDepth);
    ds.sort((a, b) => a - b);
    const lab = srgbToLab(basis[k].rgb);
    return {
      i, k, hex: basis[k].hex, rgb: basis[k].rgb,
      area: counts[k] / fgCount,
      chroma: Math.hypot(lab[1], lab[2]),
      median: pct(ds, 0.5),
    };
  });

  // 4. Boundary adjacency — who touches whom (a significant index, BG, or OTHER).
  const adj = boundaryAdjacency(slabel, w, h, sig.length);
  const totalAdj = (i) => { let t = 0; const a = adj[i] || {}; for (const key in a) t += a[key]; return t; };
  const shareToward = (i, j) => { const t = totalAdj(i); return t ? (adj[i][j] || 0) / t : 0; };

  // The same tally, re-keyed by HEX so a consumer holding only colours (a repaint chooser, a swatch
  // view) can ask "do these two tones share a boundary in this artwork?" without re-walking pixels.
  // `BG` collects transparent neighbours — at render time that is the PLATE. `OTHER` (anti-aliasing,
  // insignificant colours) is dropped: it separates every pair by a pixel or two and counting it
  // would make nothing ever touch anything.
  //
  // ⛔ Measured over the WIDE tone set, not the band analysis's population-capped `sig` (Ryan
  // 2026-08-23, Angry Panda: the mark's orange keyline is 5th of 7 tones by pixel count — a
  // keyline is definitionally THIN, so the maxLayers-by-population cut folded it into OTHER
  // before adjacency was ever measured. That blinded `wrapToneFromTouches` to exactly the tone
  // the table exists to describe, and — the ring being what faces the background — erased every
  // row's BG column with it). The depth-bin band analysis above keeps its cap (the render engine
  // caps outlines and its calibrations assume it); only the TABLE widens.
  toneTouches = (() => {
    /* ⭐ AND THE TABLE IS LABELLED AGAINST THE RASTER, NOT THE PALETTE (WD 424) — the whole census
     * fix lives in these few lines. `palette.all` deletes real inks (see the basis note above), and
     * this table is the one consumer where a deleted ink is fatal: every pixel must go SOMEWHERE,
     * so a deleted ink's mass lands on the nearest survivor and that survivor is reported wearing
     * it. Webster read `#754514` at 56% — a tone 0.66% of its pixels actually wear — because the
     * navy that owns 51% had been dropped. A table titled "every tone the mark wears" has no
     * business being built from a list that is allowed to forget one. */
    const rb = rasterBasis;
    if (rb) {
      const rlabs = rb.map((t) => srgbToLab(t.rgb));
      const rlabel = new Int16Array(n);
      const rcounts = new Array(rb.length).fill(0);
      for (let p = 0; p < n; p++) {
        if (label[p] === BG) { rlabel[p] = BG; continue; }
        const lab = srgbToLab([data[p * 4], data[p * 4 + 1], data[p * 4 + 2]]);
        let best = 0, bestD = Infinity;
        for (let k = 0; k < rlabs.length; k++) {
          const d = deltaE(lab, rlabs[k]);
          if (d < bestD) { bestD = d; best = k; }
        }
        rlabel[p] = best; rcounts[best] += 1;
      }
      // Same widening rule as below — a keyline is definitionally thin, so the table takes every
      // tone over the layer floor rather than the band analysis's population cap.
      const rsig = rcounts.map((c, k) => ({ k, c }))
        .filter((x) => x.c / fgCount >= o.minLayerFraction)
        .sort((a, b) => b.c - a.c)
        .map((x) => x.k);
      if (rsig.length >= 2) {
        const rIndexOf = new Map(rsig.map((k, i) => [k, i]));
        const rslabel = new Int16Array(n);
        for (let p = 0; p < n; p++) {
          if (rlabel[p] === BG) { rslabel[p] = BG; continue; }
          rslabel[p] = rIndexOf.has(rlabel[p]) ? rIndexOf.get(rlabel[p]) : OTHER;
        }
        const radj = boundaryAdjacency(rslabel, w, h, rsig.length);
        const redge = edgeRunFracs(rslabel, w, h, rsig.length, o.minRidgeSamples);
        const rtable = {};
        for (let i = 0; i < rsig.length; i++) {
          const a = radj[i] || {};
          const row = {}; let total = 0;
          for (const key in a) {
            const ki = Number(key);
            total += a[key];
            if (ki === OTHER) continue;
            const name = ki === BG ? 'BG' : rb[rsig[ki]].hex;
            row[name] = (row[name] || 0) + a[key];
          }
          const cell = rtable[rb[rsig[i]].hex]
            || (rtable[rb[rsig[i]].hex] = { boundary: 0, px: 0, touches: {} });
          cell.boundary += total;
          cell.px += rcounts[rsig[i]];
          if (redge[i] != null) cell.edgeFrac = redge[i];   // its width where it faces the background
          for (const k in row) cell.touches[k] = (cell.touches[k] || 0) + row[k];
        }
        return rtable;
      }
    }
    const sigWide = counts
      .map((c, k) => ({ k, c }))
      .filter((x) => x.c / fgCount >= o.minLayerFraction)
      .sort((a, b) => b.c - a.c)
      .map((x) => x.k);
    let tableSig = sig, tableAdj = adj, tableLab = slabel;
    if (sigWide.length > sig.length) {
      const wideIndexOf = new Map(sigWide.map((k, i) => [k, i]));
      const slabelWide = new Int16Array(n);
      for (let p = 0; p < n; p++) {
        if (label[p] === BG) { slabelWide[p] = BG; continue; }
        slabelWide[p] = wideIndexOf.has(label[p]) ? wideIndexOf.get(label[p]) : OTHER;
      }
      tableSig = sigWide;
      tableAdj = boundaryAdjacency(slabelWide, w, h, sigWide.length);
      tableLab = slabelWide;
    }
    const tedge = edgeRunFracs(tableLab, w, h, tableSig.length, o.minRidgeSamples);
    const table = {};
    for (let i = 0; i < tableSig.length; i++) {
      const a = tableAdj[i] || {};
      const row = {};
      let total = 0;
      for (const key in a) {
        const ki = Number(key);
        total += a[key];
        if (ki === OTHER) continue;
        const name = ki === BG ? 'BG' : basis[tableSig[ki]].hex;
        row[name] = (row[name] || 0) + a[key];
      }
      const cell = table[basis[tableSig[i]].hex]
        || (table[basis[tableSig[i]].hex] = { boundary: 0, px: 0, touches: {} });
      cell.boundary += total;
      // The tone's INK MASS, so a consumer can weigh "how much of the mark would this fuse?" —
      // a fused counter is a blemish, a fused head is the mark gone (RIGAR vs Mission).
      cell.px += counts[tableSig[i]];
      if (tedge[i] != null) cell.edgeFrac = tedge[i];
      for (const k in row) cell.touches[k] = (cell.touches[k] || 0) + row[k];
    }
    return table;
  })();

  // 5. FILL = the deepest CHROMATIC colour — the brand's hero, sitting innermost
  //    (Elite "E" teal; Bauer's blue wordmark). Neutrals (black shield, gray rim,
  //    white detail) are borders/detail, never the fill. Falls back to the deepest
  //    colour overall only if nothing is chromatic (a mono mark, which then declines
  //    below for want of a shell).
  if (o.__emitLayers) __layers = layers;
  const chromatic = layers.filter((L) => L.chroma >= o.minChroma);
  const fillPool = chromatic.length ? chromatic : layers;
  let fill = fillPool.reduce((best, L) => (L.median > best.median ? L : best), fillPool[0]);

  /* ── …EXCEPT WHEN THE NEUTRAL IS THE HERO OF THE MARK (Ryan, WD 433) ────────────────────
   * The rule above is a good default and a false universal, and two of Ryan's cards are the same
   * failure of it seen from opposite ends:
   *
   *   valley-high-school   an ORANGE keyline, a WHITE band, a BLACK "V". The only chromatic layer
   *                        is the OUTERMOST band, so it is picked as the fill, the band walk breaks
   *                        on its very first run, and the mark returns `low('no-band')` — no
   *                        structure at all. *"The logos all have 2-3 outlines to them, while the
   *                        weight numbers all only seem to have 1… the logo fill colour is black."*
   *   www-nccspartans-com  a WHITE keyline, BLACK, an ORANGE letter-band, a WHITE "N". The chromatic
   *                        layer is a MIDDLE band, so the walk stops one band early and the white
   *                        interior never becomes the fill — and rung 0 then strikes every band
   *                        colour off the number's candidates, so orange wins by elimination.
   *                        *"It's very obvious that the main fill colour for it is white."*
   *
   * ⛔ EVERY SIZE BAR FOR THIS DIED, AND THE LAST ONE DIED IN FRONT OF HIM. WD 432 asked HOW BIG the
   * neutral has to be and found no answer: sorted by the neutral's share the corpus runs 0.560,
   * 0.508, 0.453, 0.449, 0.438, 0.429 … 0.293 with no gap anywhere and his approvals interleaved
   * with his flags. It shipped a narrower rule instead — retry only where the mark has NO bands —
   * and that rule gave `ridge-view-gray-wolves-logo` a structure Ryan then REFUSED: its light grey
   * became the fill, both darks became bands, and the wolf's own linework was rescued away until it
   * washed out on the 25 and the 10. A wrong structure is worse than none (the `cherry-valley-v2`
   * lesson, third time).
   *
   * ⭐ HIS THREE ANSWERS TOGETHER MAKE THE QUESTION CATEGORICAL, AND THEN IT HAS NO NUMBER IN IT:
   *
   *     mark          neutral      area    is it the BIGGEST ink?     deepest?   Ryan
   *     valley        #010101      52.8%   YES  (next is 30.9%)       yes        after
   *     nccspartans   #FFFFFF      42.9%   YES  (next is 36.9%)       yes        after
   *     ridge-view    #E5E5E5      16.3%   NO   (#12233B is 48.5%)    yes        BEFORE
   *
   * A neutral takes the fill only when it is BOTH the deepest layer AND the largest ink — i.e. when
   * the same layer is both. That is a claim about what a logo IS ("the hero is the biggest thing in
   * the mark and it sits innermost"), not a threshold anyone tuned, which is why it fits all three
   * of his answers with room on either side where every bar did not.
   *
   * ⚠ `ridge-view-gray-wolves-logo` IS THE NAMED CONTROL FOR THIS RULE. It must keep returning
   * `low('no-band')`; if a future change gives it a structure, that change is wrong.
   * `neutralFill: false` restores the pre-WD-432 behaviour, which is how `probe-structure-decline`
   * scores BEFORE against the app's own record.
   */
  let heroPromoted = false;
  if (o.neutralFill !== false) {
    const deepest = layers.reduce((best, L) => (L.median > best.median ? L : best), layers[0]);
    const biggest = layers.reduce((best, L) => (L.area > best.area ? L : best), layers[0]);
    if (deepest === biggest && deepest !== fill && deepest.median > fill.median) {
      fill = deepest;
      heroPromoted = true;
    }
  }

  // 6. BORDER BANDS by depth-dominant peeling (Ryan's contour-trace, edge-inward):
  //    bin every foreground pixel by its depth-from-edge, and at each depth take the
  //    DOMINANT significant colour. Walking depth 0 → deep gives the concentric bands
  //    (Bauer: SILVER rim → BLACK body → …; Elite "E": RUST → WHITE → TEAL). This is
  //    robust where the fill-outward walk wasn't: a thin minority edge (Bauer's white
  //    wordmark bevel) is NEVER the dominant colour at its depth, so it drops out — the
  //    outlines mirror the BORDER, not the wordmark's own edge (Ryan 2026-07-17).
  const BINS = 32;
  const binCounts = Array.from({ length: BINS }, () => new Float64Array(sig.length));
  for (let p = 0; p < n; p++) {
    const s = slabel[p];
    if (s < 0) continue;
    const bin = Math.min(BINS - 1, Math.floor((depth[p] / maxDepth) * BINS));
    binCounts[bin][s] += 1;
  }
  // Dominant significant layer per non-empty depth bin.
  const runsRaw = [];    // every run, edge → inner, NOT deduped
  let lastDom = -1;
  for (let bin = 0; bin < BINS; bin++) {
    const bc = binCounts[bin];
    let dom = -1;
    let domV = 0;
    let tot = 0;
    for (let i = 0; i < bc.length; i++) { tot += bc[i]; if (bc[i] > domV) { domV = bc[i]; dom = i; } }
    if (!tot || dom < 0) continue;
    if (dom === lastDom) continue; // same run continues
    lastDom = dom;
    runsRaw.push(dom); // significant-layer index
  }

  /* ⭐ THE FILL'S OWN RUN IS ITS DEEPEST ONE, NOT ITS FIRST (Ryan, WD 432 — nccspartans).
   * This walk used to drop a colour that re-emerged deeper ("a colour that re-emerges deeper isn't
   * a new band") and then stop at its FIRST appearance. On a mark whose keyline and whose interior
   * are the same ink that reads the mark inside-out: nccspartans runs WHITE → black → orange →
   * WHITE, and the old walk recorded `{white, black}` with orange as the fill — the exact
   * elimination Ryan's card describes, *"none of the text fill colour on either of the 4 plates
   * used a white fill"*. Stopping at the fill's DEEPEST run instead gives `{white, black, orange}`
   * wrapping a white fill, which is his own reading of it: *"white fill with orange, black and
   * white outlines to match the logo."*
   * ⚠ The dedupe still applies to the BANDS — a colour that appears twice outside the fill is one
   * band, listed at its outermost position. Only the stop moved. */
  /* The bands for a given fill choice: every run from the edge inward, stopping at that fill's
   * DEEPEST run rather than its first. ⭐ The old walk deduped a colour that re-emerged deeper and
   * stopped at its first appearance, which reads a mark inside-out whenever its keyline and its
   * interior are the same ink. `neutralFill: false` restores it, so `probe-structure-decline.mjs`
   * can score BEFORE against the structure the app itself recorded. */
  const bandsFor = (fillIdx) => {
    /* ⭐ WHERE THE WALK STOPS, AND THE NARROW CASE IT HAD TO LEARN.
     * Normally it stops at the fill's FIRST run: everything outside the fill is a band, the fill's
     * interior is not. That is right on every mark whose fill sits strictly inside its bands.
     *
     * ⛔ IT IS WRONG WHEN THE FILL IS ALSO THE SILHOUETTE, and `www-nccspartans-com` is that mark:
     * it runs WHITE → black → orange → WHITE, a white keyline around a white "N", and once the hero
     * test above makes that white the fill, stopping at its FIRST run breaks the walk on run 0 and
     * the mark loses its structure outright. So when the fill's own ink forms run 0, the walk stops
     * at its DEEPEST run instead, which reads the mark the way Ryan does: *"white fill with orange,
     * black and white outlines to match the logo."*
     *
     * ⚠ AND THE WIDE VERSION OF THIS WAS MEASURED AND REJECTED. Stopping at the deepest run on
     * EVERY mark (`deepRunStop`, WD 432) gives nccspartans the same right answer and re-draws 13
     * more marks Ryan has approved — rendered, 24 of 29 marks moved and 19 of them were approved
     * pictures, several with their plate colours re-dealt. That is a corpus rework, not a fix. This
     * condition fires only where the fill IS the outermost run, which is the shape that needs it.
     * `deepRunStop: true` restores the wide version for measurement; `neutralFill: false` restores
     * the whole pre-WD-432 rule, which is how `probe-structure-decline` scores BEFORE.
     */
    const runs = runsRaw;   // read-only here; the band loop below dedupes as it walks
    const first = runs.indexOf(fillIdx);
    const last = runs.lastIndexOf(fillIdx);
    let stopAt;
    if (o.neutralFill === false) stopAt = first < 0 ? runs.length : first;   // pre-WD-432
    else if (o.deepRunStop) stopAt = last < 0 ? runs.length : last;          // the rejected wide form
    // The fill IS the silhouette — but ONLY for a fill the hero test promoted. Measured: without
    // that qualifier the clause also fires wherever a CHROMATIC fill happens to run at the edge and
    // recovers ELEVEN more marks Ryan has already approved, every one of them changing all four of
    // its plates. `valley-high-school` does not need this clause at all (its promoted black is not
    // run 0); `www-nccspartans-com` is the shape that does.
    else if (heroPromoted && first === 0) stopAt = last;
    else stopAt = first < 0 ? runs.length : first;

    const out = [];
    const seenBand = new Set();
    for (let i = 0; i < stopAt; i++) {
      const domIdx = runs[i];
      /* ⛔ THE FILL'S OWN HEX IS NEVER A BAND, EVEN WHEN IT RUNS AT THE EDGE — measured, and it was
       * the first thing tried. Letting it in gives `www-nccspartans-com` the white keyline Ryan
       * asked for and gives `wsuathletics-com` "green fill + {green, gold}" and `db-logo` "green
       * fill + {green}": a band list naming its own fill, which rung 0 then subtracts from the fill
       * candidates. One right answer against four degenerate ones. The mark's exposure is tested on
       * the SILHOUETTE below instead, which is what that gate was always asking about. */
      if (domIdx === fillIdx || seenBand.has(domIdx)) continue;
      seenBand.add(domIdx);
      out.push(domIdx);
      if (out.length >= o.maxLayers - 1) break;
    }
    return out;
  };

  const byIdx = new Map(layers.map((L) => [L.i, L]));
  let outerToInner = bandsFor(fill.i);
  /* ⛔ THE no-bands RETRY WD 432 SHIPPED HERE IS GONE, AND RYAN IS WHY. It re-picked the deepest
   * layer wherever the chromatic fill found no bands, kept the answer at two or more bands, and on
   * `ridge-view-gray-wolves-logo` that produced a light-grey fill wrapped by the mark's own dark
   * linework — which the rescue then painted away until the wolf washed out. He answered BEFORE on
   * it. The hero test at the fill pick replaces it and refuses ridge-view for a stated reason: its
   * deepest layer is 16.3% of the mark against a 48.5% navy, so it is not the hero, so it is not
   * the fill. Everything the retry got RIGHT (valley) the hero test gets right without it.
   */
  if (!outerToInner.length) return low('no-band');
  /* ⭐ THE SILHOUETTE IS THE OUTERMOST RUN, NOT THE OUTERMOST SURVIVING BAND (WD 432).
   * Both gates below ask a question about the ARTWORK — "is the outermost shell the exposed
   * silhouette, and is it one piece or a ring of letters?" — and they used to ask it of whichever
   * band happened to survive into the list. Those are the same layer on every mark where the fill
   * sits strictly inside its bands, which is all of them until a fill re-emerges at the rim: on
   * nccspartans the outermost run is the WHITE keyline and the first surviving band is the black
   * beneath it, which touches no background at all, so a correct read declined `outer-not-exposed
   * (0.00)`. Asking the run restores the old answer everywhere else by construction. */
  const edgeBand = runsRaw.length ? runsRaw[0] : outerToInner[0];
  const edgeBg = shareToward(edgeBand, BG);
  if (edgeBg < o.outerBgMin) return low(`outer-not-exposed(${edgeBg.toFixed(2)})`);
  // A BORDER IS ONE CONTINUOUS PIECE; LETTERING IS NOT (Ryan 2026-07-25). Garage Gym Reviews is a
  // red disc with "GARAGE GYM REVIEWS" set in a ring around it. That ring is the outermost shell and
  // faces background all the way round, so every share-based test above passes it as a border — and
  // the app then offered a "logo outline" for a logo that simply has none, lit a redundant white chip
  // beside the fill, and let a plate dye recolour lettering as though it were a keyline.
  //   Shares cannot separate the two: a keyline and a ring of letters both hug the silhouette and
  // both face the background. CONNECTEDNESS can. Verified against every logo in the drop folder —
  // this demotes exactly three, and Ryan confirmed all three genuinely have no outline: GGR, the
  // Blue Sky wordmark (words in landscape, no keyline), and a plate photo with white numbers on
  // blue. Nothing that owns a real border moves.
  const edgeDiag = componentCount(slabel, w, h, edgeBand, o.minPartPx);
  if (edgeDiag.parts >= o.letteringParts && edgeDiag.maxShare < o.letteringMaxShare) {
    return low(`outer-is-lettering(${edgeDiag.parts} parts, max ${edgeDiag.maxShare.toFixed(3)})`);
  }

  // ── DOES THE INNERMOST BAND ACTUALLY WRAP THE FILL? (Ryan 2026-08-13, Cherry Valley v2) ────────
  // 🔑 STEP 4 OF THIS MODULE'S OWN HEADER, FINALLY WIRED UP. It has said since it was written:
  // "VERIFY that order by boundary ADJACENCY (does each layer actually wrap the next one in?). This
  // is what rejects a full CREST." `fillTouchShare` below computes exactly that — and nothing has
  // ever gated on it. It was consumed only as a per-band HINT for the recolour cascade, so a mark
  // that fails the verification the header promises still came back `confidence: 'high'`.
  //
  // `Cherry-Valley-Athletic-Club-Logo-v2.jpg` is the mark that proves it must gate. A wreath badge:
  // black wreath + CV monogram + both arcs of lettering, with a rose accent in half the leaves.
  // There is no fill and no keyline anywhere in it. What the analysis returned was
  // `fill #C38F82 (20.7% of the ink) + band #020202 (79.3%)` — **the dominant ink called peelable
  // trim and a minority accent called the fill.** Three of Ryan's five reports are that one fault:
  // a black "outline" chip for a ring the plate does not have, a ✕ that peels 79% of the mark to
  // nothing, and "make the logo white" reaching only the fill because every recolour path skips
  // band-labelled pixels.
  //
  // ⛔ THE OBVIOUS RULE IS "A MAJORITY BAND IS NOT TRIM", AND IT IS FALSE. Measured across the 25
  // marks in "Logos to test": McDonald's is `fill #FDC82F 18.1% + band #D52B1E 76.3%` and that is
  // CORRECT — the arches are the fill and the red rounded-rect field genuinely is the border. A bar
  // at 50% of the ink demotes seven marks, six of them right today (McDonald's, Wexmoor 68.8%,
  // Cherry Valley **v1** 57.4%, Body State U 53.2%, Elite Edge, EE Logo). The mass ladder is a
  // continuum — 79.3 / 76.3 / 68.8 / 57.4 / 53.5 / 53.2 — with nowhere to put a bar. Trim CAN be
  // most of the mark; what it cannot do is wrap nothing.
  //
  // ⚠ SO ADJACENCY IS THE DISCRIMINATOR AND MASS IS ONLY THE DAMAGE TERM. A band that wraps nothing
  // is mis-read whatever its size, but if it is a fleck the mis-reading costs nothing — the peel
  // removes a fleck, the recolour misses a fleck. It is worth throwing a whole outline stack away
  // over only when the band IS the mark. Hence the conjunction rather than either half alone.
  //
  // ⚠ 0.02, AND IT IS MEASURED — `tools/probe-band-fill-touch.mjs` bisects `fillTouchMin` against
  // the real `analyzeStructure` to recover each band's true share (no second implementation of the
  // rule; see that file). Innermost-band shares across the 13 banded marks:
  //
  //     0.0036 ⛔ Cherry Valley v2      0.2426  Secondary PNG      0.4859  E Logo
  //     0.0450    Elite Edge Again      0.2447  Vikings            0.5131  Cinderwood
  //     0.0636    EE Logo               0.3184  Cherry Valley v1   0.6966  Springboro
  //     0.0834    Bears                 0.4200  Body State U       0.7452  McDonald's
  //     0.2037    Wexmoor
  //
  // Cherry Valley v2 is in a class of its own: 0.36% of that band's perimeter faces the fill, which
  // is not "loosely wrapping", it is "not wrapping". The next mark up is 12× higher, so any bar in
  // (0.0036, 0.0450) demotes it and nothing else. 0.02 sits in the middle of that gap.
  //
  // ⛔ NOT `fillTouchMin` (0.08), THE THRESHOLD ALREADY SITTING RIGHT HERE — and reusing it was the
  // tempting move. Two reasons not to. It was calibrated for a DIFFERENT question (may the
  // whole-mark recolour cascade swallow this band), and borrowing a constant across decisions means
  // re-tuning one silently re-tunes the other. More concretely, it does not have the clearance:
  // EE Logo sits at 0.0636 just under it and **Bears at 0.0834 just over it**, so a gate at 0.08
  // would hang a whole confidence verdict on 0.0034 — and it would demote three marks rather than
  // the one that is broken.
  //
  // 📌 THE TWO IT DELIBERATELY DOES NOT REACH, recorded with their numbers so nobody has to
  // re-derive them: **EE Logo (0.0636, band 82.5%)** — the Elite Edge lockup on charcoal, where the
  // key strips the disc interior too, so the red "E" floats inside a white ring it never touches and
  // that same white is also the wordmark; peeling it would delete the wordmark, the Broncos failure
  // exactly. And **Elite Edge Again (0.0450, band 39.0%)** — the same lockup on brick, the corpus's
  // only NEGATIVE depth slack (−0.286: the "outline" measures deeper than the fill it wraps).
  // Both look like the same species as Cherry Valley. Neither is reachable from open space, and
  // widening the bar to catch them lands it on top of Bears. They want their own evidence, not a
  // nudged threshold — the discipline `CLEAR_TIE` and `letteringMaxShare` both state.
  const bandIdxSet = new Set(outerToInner);
  // SIGNIFICANT non-band layers only. OTHER is deliberately excluded: it collects every pixel whose
  // colour isn't one of the extracted swatches, which in practice is dominated by ANTI-ALIASING
  // along the band's own outer edge. Counting it made every soft-edged band look like it abutted
  // the fill — the Garage Gym Reviews lettering ring, which touches nothing, scored as a keyline.
  const isFillSide = (k) => k >= 0 && !bandIdxSet.has(k);
  const fillTouchShare = (i) => {
    const t = totalAdj(i);
    if (!t) return 0;
    let sum = 0;
    for (const key of Object.keys(adj[i])) if (isFillSide(Number(key))) sum += adj[i][key];
    return sum / t;
  };
  /* ── DROP a BURIED innermost band before judging the chain (Ryan 2026-08-23, Abbey) ────────────
   * See `buriedBandFillMax` in DEFAULTS for the full record. A member whose boundary faces other
   * BANDS almost exclusively — under `buriedBandFillMax` toward the fill AND under
   * `buriedBandBgMax` toward the background — is interior detail the depth bins promoted, not a
   * ring. Dropped one at a time, innermost-first, never below one band; each drop moves the
   * casualty to the FILL side of the tally (the Set is live in `isFillSide`), so the next
   * member's shares are re-judged against the corrected world. The wraps-nothing demotion below
   * then applies to the innermost band that actually remains. */
  while (outerToInner.length >= 2) {
    const cand = byIdx.get(outerToInner[outerToInner.length - 1]);
    const wrap = fillTouchShare(cand.i);
    const bgFace = shareToward(cand.i, BG);
    if (wrap < o.buriedBandFillMax && bgFace < o.buriedBandBgMax) {
      outerToInner.pop();
      bandIdxSet.delete(cand.i);
    } else break;
  }
  const innerBand = byIdx.get(outerToInner[outerToInner.length - 1]);
  const innerWrap = fillTouchShare(innerBand.i);
  if (innerWrap < o.bandWrapMin && innerBand.area >= o.bandIsTheMarkArea) {
    return low(`inner-band-wraps-nothing(${innerWrap.toFixed(4)} of its edge, ${(innerBand.area * 100).toFixed(0)}% of the ink)`);
  }

  // outlines ordered INNER → OUTER (outlines[0] hugs the fill), as the render engine
  // stacks them (emitStyledText draws the outermost widest).
  const chain = outerToInner.slice().reverse().map((idx) => byIdx.get(idx));

  // 8. Thickness — each band's radial WIDTH ≈ its area ÷ the contour with its INNER
  //    neighbour (the previous chain member, or the fill). Concavity-robust (notches
  //    inflate area + contour together) and it keeps the silver RIM thin vs the black
  //    BODY, mirroring the logo. Normalised so the median outline maps to the model
  //    default; clamp + round to 0.5.
  const widthOf = (idx) => {
    const inner = idx === 0 ? fill : chain[idx - 1];
    const contour = adj[chain[idx].i] ? adj[chain[idx].i][inner.i] || 0 : 0;
    const areaPx = counts[chain[idx].k];
    return contour > 0 ? areaPx / contour : Math.sqrt(areaPx);
  };
  const widths = chain.map((_, idx) => widthOf(idx));
  const medW = widths.length ? pct(widths.slice().sort((a, b) => a - b), 0.5) : 0;
  const scale = medW > 0 ? o.defaultOutline / medW : 0;
  // Does each band ABUT THE FILL, or is it floating in its own space? (Ryan 2026-07-25.)
  //   This is what tells a keyline apart from a detached ring of lettering, and the two want
  // opposite treatment when the buyer recolours the whole logo to one tone. A keyline hugging the
  // fill exists to SEPARATE it from what's behind — paint both the same colour and the keyline
  // vanishes into the shape. A ring with clear space around it (Garage Gym Reviews' "GARAGE GYM
  // REVIEWS" around its barbell) is a distinct element; recolouring it is exactly right, and the gap
  // keeps it readable.
  //   Measured off the boundary tally already computed above: what share of this band's edge faces
  // the FILL side — any significant layer that isn't itself a band, plus OTHER (opaque interior
  // detail) — as opposed to BG. A keyline runs ~half its perimeter against the fill; a detached ring
  // faces background the whole way round, so the threshold is generous.
  //   Same principle as assignOutlineColors' MUST rule (innermost ring ≠ fill): a ring that merges
  // into its neighbour is invisible. Touching is precisely when "merges" can happen.
  //   ⚠ `bandIdxSet` / `isFillSide` / `fillTouchShare` are DEFINED ABOVE now (2026-08-13) — the
  // same measurement also gates confidence, at `inner-band-wraps-nothing`, so it has to exist before
  // the early return. Deliberately not a second copy: one definition, two readers.
  const outlines = chain.map((L, idx) => ({
    hex: L.hex,
    rgb: L.rgb,
    area: L.area,
    depth: L.median,
    touchesFill: fillTouchShare(L.i) >= o.fillTouchMin,
    thickness: scale > 0
      ? clamp(Math.round(widths[idx] * scale * 2) / 2, o.minOutline, o.maxOutline)
      : o.defaultOutline,
    /* This band's own medial-axis width as a fraction of the mark's LONG EDGE — the absolute
       `thickness` above normalises away, in the one currency `LOGO_BOX_NOMINAL_VB` converts to
       text-ring units. Null when the band carried too few ridge samples to measure. */
    widthFrac: strokeFracByLayer ? strokeFracByLayer[chain[idx].i] : null,
  }));

  // Is the CENTRE one tone? The centre = every significant layer that ISN'T a border band
  // (the fill + any interior detail). If the fill is the ONLY significant centre colour, the
  // interior is effectively one tone → the UI can just highlight that swatch instead of an
  // "Original" chip (Ryan 2026-07-22). A logo whose centre carries other ≥3% colours (purple
  // with real bits of black/green) reads as multi-tone → keep the "Original" affordance.
  const bandSet = bandIdxSet;
  const fillUniform = layers.filter((L) => !bandSet.has(L.i)).length <= 1;

  return {
    confidence: 'high',
    ...(o.__emitLayers ? { __layers } : {}),   // probe seam; see the foot of this file
    fill: { hex: fill.hex, rgb: fill.rgb, thickness: 0, area: fill.area, depth: fill.median, uniform: fillUniform },
    outlines,
    // TRUE when this mark's detail lives in its colour boundaries rather than its silhouette — see
    // `solidBlob` above. The Logo row reads it to decide whether a whole-mark flatten is an offer
    // worth making at all.
    colorBorneDetail,
    // Diagnostic only, and it must be on BOTH exits — the `low` path carries it already, and a
    // threshold measured against only half the corpus is how a bar gets set on a gap that isn't one.
    solidBlob: solidBlobDiag,
    // The artwork's characteristic stroke width, as a fraction of its long edge — drives the text
    // lockup's DEFAULT outline weight so a finely-ruled badge doesn't get a chunky ring.
    strokeFrac,
    // Per LAYER (by `sig` index), the same measurement — what each band's own linework weighs. The
    // bands carry their own copy as `widthFrac`; this is the raw table, for the probes.
    strokeFracByLayer,
    // Diagnostic only — nothing reads this to make a decision (see the note at the outer-shell check).
    edgeDiag,
    // Hex-keyed boundary adjacency (see step 4) — who touches whom, including 'BG' (the plate at
    // render time). Read by the contextual repaint choosers through `tonesTouch`.
    toneTouches,
    // The full-pixel tone census (see the declaration) — every tone the mark actually wears,
    // including thin detail under the table's 3% floor. Read by `unmeasuredPresence`.
    traceTones,
    // R13 (see the flood) — tones that ARE a detached one-tone piece of the mark. On BOTH exits,
    // because the marks that need it most (carrolllynx) return low confidence.
    soloTones,
    // The mark's ink in connected pieces (see the declaration) — what `markRecolourAllowed`'s
    // free-floating exception measures. On BOTH exits, and for a sharper reason than soloTones.
    inkPieces,
    // WD 447 — per tone, the other tones it shares a shape with. What the NEUTRAL-TOUCH rule
    // reads, and the one measurement that sees shapes the piece test is too coarse for.
    toneNeighbours,
    // WD 449 — per tone, its erosion survival and its share. What rule 3 reads to keep a
    // stroke-only minor tone out of the weight number's fill candidates.
    toneSubstance,
  };
}

// ── helpers (pure) ──────────────────────────────────────────────────────────

function clamp(v, lo, hi) { return v < lo ? lo : v > hi ? hi : v; }

/** Percentile of a PRE-SORTED ascending array (nearest-rank). */
function pct(sorted, q) {
  if (!sorted.length) return 0;
  const i = clamp(Math.round(q * (sorted.length - 1)), 0, sorted.length - 1);
  return sorted[i];
}

/**
 * Chamfer (3-4) distance transform: pixel → nearest BACKGROUND, in ~pixels
 * (returned already divided by 3). Out-of-bounds counts as background, so a mark
 * flush to the canvas edge reads that edge as silhouette. `slabel` uses BG(-1)
 * for background; any ≥0 or OTHER(-2) is foreground.
 */
/**
 * How many separate PIECES a layer is in — 4-connected flood fill over `slabel`, counting only
 * components of at least `minPx` pixels so anti-alias crumbs don't inflate the tally.
 *
 * Used to tell a BORDER from LETTERING: a keyline is continuous (1 piece, a few if a crest rim is
 * interrupted by tabs), whereas a word set around a mark is one piece per letter. Every share-based
 * test treats the two identically — both hug the silhouette, both face the background — so this is
 * the signal that separates them (Ryan 2026-07-25).
 *
 * Also reports `maxShare` — the largest piece as a fraction of the layer's pixels. That is the second
 * factor a parts-count alone lacks: a continuous keyline is ONE piece at ~1.0, while a ring of
 * letters has no dominant piece (~1/nLetters). Parts-count alone demoted 23 of 43 real logos, so the
 * two are meant to be read together.
 *
 * Iterative stack, not recursion: a big layer would blow the call stack.
 * @returns {{parts:number, maxShare:number, px:number}} components ≥ minPx, biggest piece's share
 */
function componentCount(slabel, w, h, layerIdx, minPx = 1) {
  const n = w * h;
  const seen = new Uint8Array(n);
  const stack = [];
  let parts = 0;
  let biggest = 0;
  let total = 0;
  for (let p0 = 0; p0 < n; p0++) {
    if (seen[p0] || slabel[p0] !== layerIdx) continue;
    let size = 0;
    stack.push(p0);
    seen[p0] = 1;
    while (stack.length) {
      const p = stack.pop();
      size++;
      const x = p % w;
      const y = (p / w) | 0;
      if (x > 0 && !seen[p - 1] && slabel[p - 1] === layerIdx) { seen[p - 1] = 1; stack.push(p - 1); }
      if (x < w - 1 && !seen[p + 1] && slabel[p + 1] === layerIdx) { seen[p + 1] = 1; stack.push(p + 1); }
      if (y > 0 && !seen[p - w] && slabel[p - w] === layerIdx) { seen[p - w] = 1; stack.push(p - w); }
      if (y < h - 1 && !seen[p + w] && slabel[p + w] === layerIdx) { seen[p + w] = 1; stack.push(p + w); }
    }
    total += size;
    if (size > biggest) biggest = size;
    if (size >= minPx) parts++;
  }
  return { parts, maxShare: total ? biggest / total : 0, px: total };
}

/**
 * Chamfer (3-4) distance from every foreground pixel to the nearest COLOUR BOUNDARY — a neighbouring
 * pixel of a different label, background included. Returned in ~pixels (already divided by 3).
 *
 * The twin of `chamferDepth`, and the difference is the seed set: that one measures depth from the
 * SILHOUETTE, to order concentric shells outward-in. This one measures the half-width of whatever
 * local band a pixel sits in, which is what the medial-axis stroke measurement needs — inside a wide
 * flat field it grows large, inside a hairline keyline it stays at ~0.5 no matter where that keyline
 * sits relative to the outline. OTHER counts as its own label, so an unextracted detail colour still
 * bounds the band it interrupts.
 */
function boundaryDepth(slabel, w, h) {
  const n = w * h;
  const INF = 1 << 28;
  const d = new Int32Array(n);
  const at = (x, y) => (x < 0 || y < 0 || x >= w || y >= h ? BG : slabel[y * w + x]);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const p = y * w + x;
      const s = slabel[p];
      if (s === BG) { d[p] = 0; continue; }
      const edge = at(x - 1, y) !== s || at(x + 1, y) !== s || at(x, y - 1) !== s || at(x, y + 1) !== s;
      d[p] = edge ? 0 : INF;
    }
  }
  const get = (x, y) => (x < 0 || y < 0 || x >= w || y >= h ? 0 : d[y * w + x]);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const p = y * w + x;
      if (d[p] === 0) continue;
      let m = d[p];
      const a = get(x - 1, y) + 3, b = get(x, y - 1) + 3;
      const c = get(x - 1, y - 1) + 4, e = get(x + 1, y - 1) + 4;
      if (a < m) m = a; if (b < m) m = b; if (c < m) m = c; if (e < m) m = e;
      d[p] = m;
    }
  }
  for (let y = h - 1; y >= 0; y--) {
    for (let x = w - 1; x >= 0; x--) {
      const p = y * w + x;
      if (d[p] === 0) continue;
      let m = d[p];
      const a = get(x + 1, y) + 3, b = get(x, y + 1) + 3;
      const c = get(x + 1, y + 1) + 4, e = get(x - 1, y + 1) + 4;
      if (a < m) m = a; if (b < m) m = b; if (c < m) m = c; if (e < m) m = e;
      d[p] = m;
    }
  }
  const out = new Float32Array(n);
  for (let p = 0; p < n; p++) out[p] = slabel[p] === BG ? 0 : d[p] / 3 + 0.5;
  return out;
}

/**
 * HOW DEEP EACH TONE RUNS INWARD FROM THE SILHOUETTE, where it faces the background (WD 476).
 *
 * Ryan on svsucardinals: *"copied white ring is heavier than the logo's hairline"*. The wrap was the
 * one edge with no width of its own: `presentedLogoStack` priced it at the mark's MEDIAN stroke, and
 * the medial-axis pass cannot answer for it - on ncurams the wrap tone is also the 59% body, so its
 * layer median is the body's width, not the edge's. So measure the edge where it IS the edge: from
 * every pixel of the tone that touches the background, walk inward (up the silhouette depth) while
 * the pixel stays that tone, and take the median run. svsu and ncurams both measure 1 px of 200 -
 * the hairline in each artwork. Fraction of the long edge, like every other width here; null under
 * `minSamples`, so a tone with too little frontier falls back exactly as before.
 */
function edgeRunFracs(lab, w, h, k, minSamples) {
  const depth = chamferDepth(lab, w, h);
  const runs = Array.from({ length: k }, () => []);
  const isBg = (x, y) => x < 0 || y < 0 || x >= w || y >= h || lab[y * w + x] === BG;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const t = lab[y * w + x];
      if (t < 0 || !(isBg(x - 1, y) || isBg(x + 1, y) || isBg(x, y - 1) || isBg(x, y + 1))) continue;
      let cx = x, cy = y, run = 0;
      while (run < 64 && lab[cy * w + cx] === t) {
        run += 1;
        let best = -1, bd = depth[cy * w + cx];
        for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
          const u = cx + dx, v = cy + dy;
          if (u < 0 || v < 0 || u >= w || v >= h) continue;
          if (depth[v * w + u] > bd) { bd = depth[v * w + u]; best = v * w + u; }
        }
        if (best < 0) break;
        cx = best % w; cy = (best - cx) / w;
      }
      runs[t].push(run);
    }
  }
  return runs.map((r) => {
    if (r.length < minSamples) return null;
    r.sort((a, b) => a - b);
    return pct(r, 0.5) / Math.max(w, h);
  });
}

function chamferDepth(slabel, w, h) {
  const n = w * h;
  const INF = 1 << 28;
  const d = new Int32Array(n);
  for (let p = 0; p < n; p++) d[p] = slabel[p] === BG ? 0 : INF;
  const at = (x, y) => (x < 0 || y < 0 || x >= w || y >= h ? 0 : d[y * w + x]); // OOB = bg (0)
  // forward
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const p = y * w + x;
      if (d[p] === 0) continue;
      let m = d[p];
      const a = at(x - 1, y) + 3, b = at(x, y - 1) + 3;
      const c = at(x - 1, y - 1) + 4, e = at(x + 1, y - 1) + 4;
      if (a < m) m = a; if (b < m) m = b; if (c < m) m = c; if (e < m) m = e;
      d[p] = m;
    }
  }
  // backward
  for (let y = h - 1; y >= 0; y--) {
    for (let x = w - 1; x >= 0; x--) {
      const p = y * w + x;
      if (d[p] === 0) continue;
      let m = d[p];
      const a = at(x + 1, y) + 3, b = at(x, y + 1) + 3;
      const c = at(x + 1, y + 1) + 4, e = at(x - 1, y + 1) + 4;
      if (a < m) m = a; if (b < m) m = b; if (c < m) m = c; if (e < m) m = e;
      d[p] = m;
    }
  }
  const out = new Float32Array(n);
  for (let p = 0; p < n; p++) out[p] = d[p] / 3;
  return out;
}

/**
 * For each significant layer, tally what its BOUNDARY pixels sit next to
 * (4-connected): another significant layer's index, BG (background / OOB), or
 * OTHER. Returns adj[i] = { [neighborKey]: count }. Only pixels that actually lie
 * on a boundary (touch something not their own layer) contribute.
 */
function boundaryAdjacency(slabel, w, h, nSig) {
  const adj = Array.from({ length: nSig }, () => ({}));
  const at = (x, y) => (x < 0 || y < 0 || x >= w || y >= h ? BG : slabel[y * w + x]);
  const bump = (i, key) => { adj[i][key] = (adj[i][key] || 0) + 1; };
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const s = slabel[y * w + x];
      if (s < 0) continue; // background or OTHER pixel is not a layer boundary owner
      const ns = [at(x - 1, y), at(x + 1, y), at(x, y - 1), at(x, y + 1)];
      for (const nb of ns) {
        if (nb === s) continue; // same layer — interior, not a boundary
        bump(s, nb); // nb is another sig index (≥0), BG(-1), or OTHER(-2)
      }
    }
  }
  return adj;
}

// ===========================================================================
// BROWSER-ONLY: decode + downscale + getImageData wrapper
// ===========================================================================

/**
 * Extract fill/outline structure from a CUT-OUT logo source (background already
 * transparent). Resolves (never rejects) — any failure returns a low-confidence
 * result so the caller just falls back to the plain palette styling.
 *
 * @param {HTMLImageElement|HTMLCanvasElement|ImageBitmap} imageSource
 * @param {{all?:Array<{hex:string,rgb:[number,number,number]}>}} palette
 * @param {Partial<typeof DEFAULTS> & {maxEdge?:number}} [opts]
 * @returns {Promise<LogoStructure>}
 */
export async function extractLogoStructure(imageSource, palette, opts = {}) {
  const low = (reason) => ({ confidence: 'low', fill: null, outlines: [], reason });
  try {
    const swatches = (palette && Array.isArray(palette.all) ? palette.all : []).filter(
      (s) => s && s.hex && Array.isArray(s.rgb),
    );
    if (swatches.length < 2) return low('too-few-swatches');
    const maxEdge = opts.maxEdge || 220; // fine enough to resolve a 1px outline ring
    const sw = imageSource.naturalWidth || imageSource.width || 0;
    const sh = imageSource.naturalHeight || imageSource.height || 0;
    if (!sw || !sh) return low('zero-dims');
    const scale = Math.min(1, maxEdge / Math.max(sw, sh));
    const dw = Math.max(1, Math.round(sw * scale));
    const dh = Math.max(1, Math.round(sh * scale));
    const canvas =
      typeof OffscreenCanvas !== 'undefined'
        ? new OffscreenCanvas(dw, dh)
        : Object.assign(document.createElement('canvas'), { width: dw, height: dh });
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return low('no-context');
    // Nearest-neighbour (smoothing OFF) — a smoothed shrink blends the shell
    // boundaries into intermediate colours that mislabel the layering.
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(imageSource, 0, 0, dw, dh);
    const imageData = ctx.getImageData(0, 0, dw, dh);
    return analyzeStructure(imageData, swatches, opts);
  } catch (error) {
    return low('exception');
  }
}

export default extractLogoStructure;

// ============================================================================
// WHERE THE ARTWORK ISN'T — the clear spot (Ryan 2026-07-30)
// ============================================================================
// *"On Full Face, it needs to identify where the blank space is, and place the number there."*
//
// Full Face bleeds a mark across the whole cap, so there is no field left to give a weight number:
// wherever it goes it goes ON the artwork. Every other layout answers this by carving room, and this
// one cannot — the bleed is the layout. So instead of choosing a place and hoping, it MEASURES the
// mark and puts the number where the mark is thinnest.
//
// ⚠ THIS RUNS AT UPLOAD, NOT AT PAINT, and that is not an optimisation — the render path is
// deliberately DOM-free (it has to run identically in a node test, in the 2D preview and in the 3D
// texture bake), so it can never look at pixels. The answer is measured once, stored on the logo
// beside `aspectRatio`, and carried through serialization like any other fact about the artwork.
//
// The result is in the IMAGE's own normalised box (u, v ∈ −1..1, +v DOWN as in SVG), so a consumer
// maps it through whatever box it happens to be drawing the logo into.
// ============================================================================

/** Alpha below this reads as "no ink here". Generous: an anti-aliased edge is not coverage. */
const CLEAR_ALPHA = 32;
/** How much of the logo's box the number needs, as a share of the box's WIDTH. Matches the cap's
 *  own number box against a full-face mark closely enough to choose between candidate spots. */
const CLEAR_BOX_FRAC = 0.26;
/** Coarse grid the search runs on. 48 resolves a gap the size of the number to about half a
 *  character; finer costs time at upload for a decision measured in whole characters. */
const CLEAR_GRID = 48;
/**
 * How close two candidates' coverage has to be before POSITION is allowed to choose between them.
 *
 * ⛔ THIS EXISTS BECAUSE THE TIE-BREAK WAS NOT ONE (Ryan 2026-08-06, on the Body State U mark:
 * *"why the weight number appears where it does… and not in the bigger open area/gap?"*).
 *
 * The score used to be `coverage + 0.12·(|v−preferV|/2 + |u|/2)` — one number, with position added
 * straight onto ink. Its own comment claimed the 0.12 was "weak enough that a measurably clearer
 * spot always wins over a better-placed one", and that was simply not true: the positional term
 * ranges over ~0.084, which is wider than the coverage spread of most artwork, so it did not break
 * ties, it usually decided. Measured on his logo (tools/probe-clearspot-bodystate.mjs):
 *
 *     gap between the U's ascenders   9.33% ink   + 0.0845 position  = 0.1778
 *     inside the U's bowl            14.05% ink   + 0.0344 position  = 0.1749   ← shipped
 *
 * The bowl won by 0.003 while carrying HALF AS MUCH INK AGAIN. So coverage is now a gate, not a
 * summand: only spots within `CLEAR_TIE` of the cleanest are eligible, and `preferV` chooses among
 * those. "Centre bottom if it fits" still holds wherever the artwork is genuinely blank in several
 * places, which is the case it was written for.
 *
 * ⚠ 0.01, AND THE FIGURE IS MEASURED RATHER THAN CHOSEN (tools/probe-clearspot-sweep.mjs, over the
 * 18 marks in "Logos to test"). One percentage point of the box inked is a couple of anti-aliased
 * rows — below any difference a designer would call real. The first cut used 0.02, which is still
 * a small number and was already too loose: it let the Wallabies mark (+1.7pp) and Secondary PNG
 * (+1.8pp) slide onto measurably dirtier spots for the sake of position, and the Wallabies is the
 * case the whole scan was written for. At 0.01 the sweep moves exactly the two marks that were
 * wrong — Body State U (4.7pp cleaner) and BauerRevamp (5.0pp cleaner) — and leaves the other
 * sixteen untouched.
 *   Widening this is not free: every extra point of slack is permission to sit on more ink.
 */
const CLEAR_TIE = 0.01;
/**
 * Above this, the scan has NOTHING TO SAY and hands the answer to `preferV` (Ryan 2026-08-12).
 *
 * `CLEAR_TIE` breaks ties between spots that are genuinely clear. It cannot help when NO spot is
 * clear, and there the search degenerates in a way that looks like an answer: on a mark that covers
 * the whole face every candidate is ~100% inked, so "least covered" is decided entirely by which box
 * hangs furthest off the artwork's edge. That is a sliver at the RIM — the number would sit half off
 * the mark — and it is why a full-face disc answered at the vertical centre instead of the bottom.
 *
 * ⚠ THIS IS NOT A RARE EDGE CASE, WHICH IS THE REAL FINDING. The ink mask reads the ALPHA channel,
 * so any mark without transparency is 100% inked everywhere by definition. Measured over the 22
 * marks in "Logos to test": **14 of them report a cleanest of exactly 1.000** — every JPEG and every
 * opaque WebP, including Cherry Valley, Cinderwood, Ridge View and Wexmoor, i.e. the showcase set.
 * Each was getting a rim answer that meant nothing.
 *
 * ⚠ 0.9 IS MEASURED, NOT CHOSEN — the same discipline as `CLEAR_TIE`. The corpus splits cleanly:
 * every mark carrying real alpha lands between 0.000 and 0.650 (Philadelphia PD is the densest), the
 * synthetic full-face disc lands at 0.974, and the opaque marks at 1.000. Any figure in (0.65, 0.974]
 * separates "the measurement means something" from "it does not"; 0.9 sits in the middle of that gap
 * with room on both sides. Lowering it toward 0.65 would start overriding marks that DO have a
 * measurable gap, which is the whole value of the scan.
 *
 * Falling back to `preferV` is not a compromise — `render-engine`'s own note says the preset's slot
 * is "the bottom of the face, which is the right default anyway".
 */
const CLEAR_SATURATED = 0.9;
/**
 * Bumped whenever the SCAN changes, so a stored answer measured by an older build is re-measured
 * instead of outliving the rule that produced it. See `backfillClearSpot`.
 *
 *   1 — the original single-score search (2026-07-30)
 *   2 — coverage gates, position breaks ties (2026-08-06)
 *   3 — a SATURATED mark (nothing clear anywhere) falls back to `preferV` instead of the rim
 *       (2026-08-12). Must re-measure: every opaque mark — 14 of the 22 in the test folder — had a
 *       stored answer from the degenerate branch.
 */
export const CLEAR_SPOT_VERSION = 3;

/**
 * The emptiest place to put a small box on this artwork.
 *
 * @param {{width:number,height:number,data:Uint8ClampedArray|number[]}} imageData
 * @param {{boxFrac?:number, grid?:number, preferV?:number}} [opts] — `preferV` is where the answer
 *   should land when the art is genuinely blank in several places (Ryan's stated default for the
 *   badge case: *"center bottom if it fits"*), as a v in −1..1.
 * @returns {{u:number, v:number, coverage:number}|null} null when there is nothing to measure.
 */
export function findClearSpot(imageData, opts = {}) {
  const src = imageData || null;
  if (!src || !src.width || !src.height || !src.data) return null;
  const boxFrac = opts.boxFrac || CLEAR_BOX_FRAC;
  const G = Math.max(8, Math.round(opts.grid || CLEAR_GRID));
  const preferV = Number.isFinite(opts.preferV) ? opts.preferV : 0.72;
  const { width: W, height: H, data } = src;

  // ── ink mask, then a summed-area table so every candidate costs four lookups ──────────────────
  const ink = new Float32Array(G * G);
  for (let gy = 0; gy < G; gy++) {
    const y0 = Math.floor((gy * H) / G);
    const y1 = Math.max(y0 + 1, Math.floor(((gy + 1) * H) / G));
    for (let gx = 0; gx < G; gx++) {
      const x0 = Math.floor((gx * W) / G);
      const x1 = Math.max(x0 + 1, Math.floor(((gx + 1) * W) / G));
      let hit = 0;
      let n = 0;
      for (let y = y0; y < y1; y++) {
        for (let x = x0; x < x1; x++) {
          if (data[(y * W + x) * 4 + 3] > CLEAR_ALPHA) hit += 1;
          n += 1;
        }
      }
      ink[gy * G + gx] = n ? hit / n : 0;
    }
  }
  // Nothing on the canvas at all → no opinion. (A fully-inked one still answers: the LEAST covered
  // spot is a real answer even when every spot is covered, which is the Full Face case by definition.)
  let total = 0;
  for (let i = 0; i < ink.length; i++) total += ink[i];
  if (total <= 0) return null;

  const sat = new Float32Array((G + 1) * (G + 1));
  for (let y = 0; y < G; y++) {
    for (let x = 0; x < G; x++) {
      sat[(y + 1) * (G + 1) + (x + 1)] =
        ink[y * G + x] + sat[y * (G + 1) + (x + 1)] + sat[(y + 1) * (G + 1) + x] - sat[y * (G + 1) + x];
    }
  }
  const sum = (x0, y0, x1, y1) =>
    sat[y1 * (G + 1) + x1] - sat[y0 * (G + 1) + x1] - sat[y1 * (G + 1) + x0] + sat[y0 * (G + 1) + x0];

  const bw = Math.max(2, Math.round(G * boxFrac));
  const bh = Math.max(2, Math.round(bw * 0.6)); // a weight lockup is wider than it is tall
  /* ── TWO PASSES: HOW CLEAR, THEN WHERE (see CLEAR_TIE) ────────────────────────────────────────
     Every candidate is scored on ink alone, the cleanest sets the bar, and position only chooses
     among the spots that reach it. Two passes over the same grid rather than one pass over a
     collected array: every lookup is four reads off the summed-area table, so the scan is cheap
     twice, and nothing has to be held in memory to be sorted afterwards. */
  const candidates = [];
  let cleanest = Infinity;
  for (let y = 0; y + bh <= G; y++) {
    for (let x = 0; x + bw <= G; x++) {
      const u = ((x + bw / 2) / G) * 2 - 1;
      const v = ((y + bh / 2) / G) * 2 - 1;
      // ⚠ THE MARK IS CLIPPED TO THE CAP'S DISC, so a corner of the image box is not a place at all —
      // it is off the face. Candidates are held inside the inscribed circle, with room for the box.
      const rad = Math.hypot(u, v) + Math.hypot(bw / G, bh / G);
      if (rad > 1) continue;
      const coverage = sum(x, y, x + bw, y + bh) / (bw * bh);
      if (coverage < cleanest) cleanest = coverage;
      candidates.push({ u, v, coverage });
    }
  }
  if (!candidates.length) return null;   // every box ran off the disc — nothing to say
  // The bar, then the taste. `preferV` is Ryan's stated default for the badge case ("centre bottom
  // if it fits") and it decides among equals only.
  const bar = cleanest + CLEAR_TIE;
  // Nothing is clear → the coverage gate is meaningless and every candidate is eligible, so `preferV`
  // decides alone. See CLEAR_SATURATED: the alternative is a sliver at the rim that is "clearest"
  // only because it hangs off the artwork.
  const saturated = cleanest >= CLEAR_SATURATED;
  let best = null;
  for (const c of candidates) {
    if (!saturated && c.coverage > bar) continue;
    const place = Math.abs(c.v - preferV) / 2 + Math.abs(c.u) / 2;
    if (!best || place < best.place) best = { ...c, place };
  }
  return best ? { u: best.u, v: best.v, coverage: best.coverage } : null;
}

/**
 * Browser entry point for `findClearSpot` — decode, downscale, measure.
 *
 * Separate from `extractLogoStructure` on purpose, and called for EVERY logo rather than only for a
 * confidently-palettised one: structure analysis needs two clean swatches to say anything, whereas
 * "where is there no ink" is answerable from the alpha channel of any artwork at all. Gating this
 * behind the same condition would have left the common case — a photo-traced mascot, a low-confidence
 * crest — with the number back in a fixed place, which is the bug it exists to fix.
 */
export async function extractClearSpot(imageSource, opts = {}) {
  try {
    const maxEdge = opts.maxEdge || 160; // the search is coarse; more pixels buy nothing
    const sw = imageSource.naturalWidth || imageSource.width || 0;
    const sh = imageSource.naturalHeight || imageSource.height || 0;
    if (!sw || !sh) return null;
    const scale = Math.min(1, maxEdge / Math.max(sw, sh));
    const dw = Math.max(1, Math.round(sw * scale));
    const dh = Math.max(1, Math.round(sh * scale));
    const canvas =
      typeof OffscreenCanvas !== 'undefined'
        ? new OffscreenCanvas(dw, dh)
        : Object.assign(document.createElement('canvas'), { width: dw, height: dh });
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(imageSource, 0, 0, dw, dh);
    return findClearSpot(ctx.getImageData(0, 0, dw, dh), opts);
  } catch (error) {
    return null; // a mark we cannot measure simply keeps the default placement
  }
}

/**
 * BACKFILL: measure the clear spot for a logo that arrived without one.
 *
 * ⚠ THIS IS NOT A NICETY, it is the difference between the feature working and the feature working
 * FOR NEW UPLOADS ONLY (Ryan 2026-07-30, testing with the Wallabies mark: *"it needs to be in the
 * empty space (top left) but it's smack middle bottom on the logo"*). The scan itself was finding
 * the corner correctly — his logo simply predated it. Every design already in a browser, every
 * resumed autosave and every shared link carries a logo measured before this existed, and each of
 * them would have kept the fixed placement for ever with nothing on screen to explain why.
 *
 * Idempotent and cheap to ask: it returns false immediately once a spot is present, so it can be
 * called from a refresh path that runs on every commit.
 *
 * @returns {Promise<boolean>} whether it wrote anything (i.e. whether the caller should re-render).
 */
export async function backfillClearSpot(profile) {
  const p = profile || null;
  if (!p) return false;
  const els = Array.isArray(p.elements) ? p.elements : [];
  // Every logo slot wears the same mark, and `savedLogo` is the same mark again — so one
  // measurement serves all of them, and the first record with artwork is as good as any.
  const targets = [...els.filter((e) => e && e.type === 'logo' && e.logo && e.logo.dataUrl).map((e) => e.logo)];
  if (p.savedLogo && p.savedLogo.dataUrl) targets.push(p.savedLogo);
  /* ⛔ "STALE" COUNTS AS MISSING (2026-08-06). This read `!l.clearSpot`, so a spot measured by an
     older build outlived the rule that produced it for ever — and that made a scan fix reach NEW
     UPLOADS ONLY. The logos already sitting in Ryan's session, every autosave, and every shared
     link would have kept the placement the old search chose, with nothing on screen to explain why
     two identical marks landed their numbers differently. Exactly the failure this function was
     written for in the first place (see the header), one version along.
       Still idempotent and still cheap to ask: once every record carries the CURRENT version this
     returns false on the first line, so the every-commit refresh path is unaffected. */
  const stale = (l) => !l.clearSpot || l.clearSpot.ver !== CLEAR_SPOT_VERSION;
  const needy = targets.filter(stale);
  if (!needy.length) return false;
  const src = needy[0].dataUrl;
  const img = await new Promise((resolve) => {
    try {
      const im = new Image();
      im.onload = () => resolve(im);
      im.onerror = () => resolve(null);
      im.src = src;
    } catch (e) { resolve(null); }
  });
  if (!img) return false;
  const spot = await extractClearSpot(img);
  if (!spot) return false;
  // Stamped on EVERY record carrying this artwork, not just the one measured: they are the same
  // mark, and leaving one behind would make the answer depend on which slot the renderer read.
  for (const l of targets) {
    if (stale(l) && l.dataUrl === src) l.clearSpot = { u: spot.u, v: spot.v, ver: CLEAR_SPOT_VERSION };
  }
  return true;
}

/* ── PROBE SEAM (WD 432) ─────────────────────────────────────────────────────────────────────────
 * `probe-structure-decline.mjs` needs the two numbers the fill pick turns on — how deep and how big
 * the deepest layer is against the chromatic one — so the bar can be read off a measured table
 * rather than chosen. Re-running the whole analysis for them is wasteful but honest: it is the
 * REAL layer table, not a second implementation of it, which is the trap `probe-pocket-shape`
 * documents. Nothing in the app calls this. */
export function __layersForProbe(imageData, swatches, opts = {}) {
  const st = analyzeStructure(imageData, swatches, { ...opts, __emitLayers: true });
  const L = st.__layers;
  if (!L || !L.length) return {};
  const o = { ...DEFAULTS, ...opts };
  const deepest = L.reduce((b, x) => (x.median > b.median ? x : b), L[0]);
  const chrom = L.filter((x) => x.chroma >= o.minChroma);
  const pool = chrom.length ? chrom : L;
  const chromFill = pool.reduce((b, x) => (x.median > b.median ? x : b), pool[0]);
  return {
    deepestHex: deepest.hex, deepestArea: deepest.area, deepestMedian: deepest.median,
    chromHex: chromFill.hex, chromArea: chromFill.area, chromMedian: chromFill.median,
  };
}
